// Người duyệt (owner chốt 2026-09-30): mỗi việc chọn được MỘT người duyệt; tài khoản của người đó vào trang "Cần duyệt" thấy
// ngay các việc chờ mình và bấm Duyệt / Trả lại. Hàm thuần — dùng ở server (functions/api/viec/[so]/duyet.ts) lẫn giao diện,
// test ở tests/duyet.test.ts.
//
// Ba hành động, khớp nghĩa nút sẵn có ở ngăn việc (Duyệt & đóng / Trả lại) và thêm một cách "duyệt mà việc còn phải làm tiếp":
//   duyet-dong  Duyệt & đóng           → comment “Đã duyệt & đóng” + đóng issue (giống hd.duyet)
//   duyet-tiep  Duyệt, làm tiếp        → comment “Đã duyệt — làm tiếp” + về tt:san-sang (việc vẫn mở, người làm nhận tiếp)
//   tra-lai     Trả lại                → comment lý do (BẮT BUỘC) + về tt:san-sang (giống hd.traLai)
import type { VaiTro } from './kieu';

export const HANH_DONG_DUYET = ['duyet-dong', 'duyet-tiep', 'tra-lai'] as const;
export type HanhDongDuyet = (typeof HANH_DONG_DUYET)[number];
export const TOI_DA_GHI_CHU = 2000;

export const TEN_HANH_DONG: Record<HanhDongDuyet, string> = {
  'duyet-dong': 'Duyệt & đóng',
  'duyet-tiep': 'Duyệt, làm tiếp',
  'tra-lai': 'Trả lại',
};

// Câu gợi ý ghi chú nhanh (chip dưới ô ghi chú) — bấm là điền, sửa được trước khi gửi.
export const GHI_CHU_NHANH: Record<'duyet' | 'tra-lai', string[]> = {
  duyet: ['Đồng ý.', 'Đồng ý, triển khai theo kế hoạch.', 'Đồng ý, làm tiếp bước sau.'],
  'tra-lai': ['Cần bổ sung: ', 'Chưa đúng ý, sửa lại: ', 'Cần số liệu / nguồn kèm theo: ', 'Trao đổi thêm với tôi trước.'],
};

export function kiemDuyet(than: unknown): { du_lieu: { hanh_dong: HanhDongDuyet; ghi_chu: string } } | { loi: string } {
  if (!than || typeof than !== 'object' || Array.isArray(than)) return { loi: 'than-sai' };
  const j = than as Record<string, unknown>;
  if (!HANH_DONG_DUYET.includes(j.hanh_dong as HanhDongDuyet)) return { loi: 'hanh-dong-sai' };
  const hanh_dong = j.hanh_dong as HanhDongDuyet;
  const ghi_chu = j.ghi_chu === undefined || j.ghi_chu === null ? '' : typeof j.ghi_chu === 'string' ? j.ghi_chu.trim() : null;
  if (ghi_chu === null) return { loi: 'ghi-chu-sai' };
  if (ghi_chu.length > TOI_DA_GHI_CHU) return { loi: 'ghi-chu-qua-dai' };
  if (hanh_dong === 'tra-lai' && !ghi_chu) return { loi: 'thieu-ly-do' };
  return { du_lieu: { hanh_dong, ghi_chu } };
}

// Comment để lại trên issue (server gắn thêm chữ ký người duyệt bằng dongGoi). Câu “Trả lại” giữ nguyên chữ của hd.traLai.
export function binhLuanDuyet(hanh_dong: HanhDongDuyet, ghi_chu: string): string {
  const g = ghi_chu.trim();
  if (hanh_dong === 'tra-lai') return `↩ **Trả lại để làm tiếp:**\n\n${g}`;
  const dau = hanh_dong === 'duyet-dong' ? '✅ **Đã duyệt & đóng.**' : '✅ **Đã duyệt — làm tiếp.**';
  return g ? `${dau}\n\n${g}` : dau;
}

// Thân lệnh PATCH issue cho từng hành động. `nhan` = tên các nhãn HIỆN CÓ của issue (giữ nguyên mọi nhãn khác, chỉ thay trục tt:).
export function thanSauDuyet(hanh_dong: HanhDongDuyet, nhan: string[]): Record<string, unknown> {
  if (hanh_dong === 'duyet-dong') return { state: 'closed', state_reason: 'completed' };
  return { labels: [...nhan.filter((n) => !n.startsWith('tt:')), 'tt:san-sang'] };
}

export type IssueDuyet = { state?: unknown; labels?: unknown };
export const tenNhanCuaIssue = (i: IssueDuyet): string[] =>
  (Array.isArray(i.labels) ? i.labels : []).map((l) => (typeof l === 'string' ? l : (l as { name?: unknown })?.name)).filter((n): n is string => typeof n === 'string');

// Việc phải còn MỞ và đang `tt:cho-duyet` mới duyệt / trả lại được — tránh bấm lần hai (hoặc người khác đã xử lý) làm nhảy trạng thái lung tung.
export function kiemDangChoDuyet(i: IssueDuyet): 'da-dong' | 'khong-cho-duyet' | null {
  if (i.state !== 'open') return 'da-dong';
  return tenNhanCuaIssue(i).includes('tt:cho-duyet') ? null : 'khong-cho-duyet';
}

// Ai được duyệt: đúng người duyệt của việc đó, hoặc quản trị viên. Người duyệt không cần quyền sửa việc — chỉ duyệt / trả lại / comment.
export const duocDuyet = (toi: { id: number; vai_tro: VaiTro }, nguoi_duyet: number | null) =>
  toi.vai_tro === 'admin' || (nguoi_duyet !== null && toi.id > 0 && toi.id === nguoi_duyet);

export function cauThongBaoDuyet(hanh_dong: HanhDongDuyet, ten: string, so: number): string {
  if (hanh_dong === 'tra-lai') return `${ten} trả lại việc #${so}`;
  return hanh_dong === 'duyet-dong' ? `${ten} đã duyệt & đóng việc #${so}` : `${ten} đã duyệt việc #${so} — làm tiếp`;
}
